import { METRICS } from '@/models/metrics.enum';

import { omitWhenFeatureFlag, requireFeatureFlag } from './utils';

export const tooltips = {
  noBrandPermission: "Looks like this feature isn't included in your plan!",
  noUserPermission: "Looks like you don't have permission to use this feature!",
  connectCallToAction:
    "Dash Social brings you all of the insights you love, including Facebook! Ask your Dash Social administrator to provide you access. Or if this feature isn't in your package, contact your Customer Success Representative to try it out!",
  avgEngagementRate:
    'The average engagement rate for your organic posts published during the selected date range\n' +
    '\n' +
    'Engagement Rate = Total Engagements / Impressions',
  averageEngagementRatePublic:
    'The Total Engagements / Page Fans of posts published during the Reporting Period. This excludes Post Clicks and Impressions to ensure a fair comparison with competitors based on publicly available data.',
  averageEffectiveness:
    'The average effectiveness rate for your organic posts published during the selected date range\n' +
    '\n' +
    'Effectiveness Rate = (Total Engagements + Video Views) / Reach\n',
  linkClicks:
    'The total number of link clicks for your organic posts published during the selected date range',
  netNewFans: 'The net total of new fans your page has accumulated during the selected date range',
  netNewFollowers:
    'The net total of new followers your page has accumulated during the selected date range',
  avgDailyPostReach:
    "The average daily reach for any of your page's organic posts during the selected date range",
  engagements:
    "The number of total engagements on any of your page's organic and paid posts during the selected date range",
  impressionsTooltip: `The total number of impressions for any of your page's organic posts during the selected date range`,
};

export const promotedTopLineStatsTooltipsDeprecated = {
  engagements:
    'The total number of engagements on your organic posts published during the selected date range' +
    '\n\n' +
    'Engagements = Reactions + Comments + Shares + Post Clicks',
  avgEngagementRate:
    'The average engagement rate for your organic posts published during the selected date range' +
    '\n\n' +
    'Engagement Rate = Engagements / Impressions',
  avgEffectiveness:
    'The average effectiveness rate for your organic posts published during the selected date range' +
    '\n\n' +
    'Effectiveness Rate = (Engagements + Video Views) / Reach',
  avgReach:
    "The average reach for any of your page's organic and promoted posts published during the selected date range" +
    '\n\n' +
    'Average Reach = Total Reach / Number of Posts published during the time frame',
  impressions:
    'The total number of impressions for your organic and promoted posts published during the selected date range',
  linkClicks:
    'The total number of link clicks for your organic and promoted posts published during the selected date range',
};

export const promotedTopLineStatsTooltips = {
  engagements:
    'The sum of engagements on your organic and promoted posts' +
    '\n\n' +
    'Engagements = Reactions + Shares + Comments' +
    '\n\n' +
    'Reactions include Likes, Love, Haha, Wow, Sad, Angry and Care',
  avgEngagementRate:
    'The average engagement rate for your organic and promoted posts' +
    '\n\n' +
    'Engagement Rate = (Engagements / Views) / Number of Posts',
  avgEffectiveness:
    'The average percentage of users that saw your organic and promoted posts and engaged with it' +
    '\n\n' +
    'Average Effectiveness = (Engagements / Reach) / Number of Posts',
  avgReach:
    'The average reach of your organic and promoted posts' +
    '\n\n' +
    'Avg. Reach = Reach / Number of Posts',
  views:
    'The number of times your organic and promoted videos were played for at least 1ms plus the number of times your photo and text posts were on screen',
  linkClicks:
    'The number of times a Facebook user clicked and followed the link, or link preview to the destination URL on your organic and promoted posts',
  avgViewers: ({ startDate } = {}) => {
    const tooltip =
      'The average number of unique accounts that have viewed your posts at least once.' +
      '\n\n' +
      'Avg. Viewers = Viewers / Number of Posts';

    if (startDate?.isBefore('2025-07-31', 'day')) {
      return `${tooltip}\n\nNote: Before July 31, 2025, this value is legacy Reach.`;
    }
    return tooltip;
  },
};

export const promotedMediaPopupTooltips = {
  amountSpent:
    "The total amount of money you've spent boosting your posts. Recent spend may be estimated for up to 48 hours.",
  clicks:
    'The total number of clicks this promoted post received. This can include link clicks, clicks to your profile, and more.',
  comments: 'The number of comments your posts received',
  commentsOrganic: 'The number of organic comments your posts received',
  commentsPromoted: 'The number of promoted comments your posts received',
  costPerThruPlay:
    'The average cost per ThruPlay for this video\n' +
    '\n' +
    'Cost Per ThruPlay = Amount spent / ThruPlays',
  cpc:
    'The average cost per click on your promoted posts\n' +
    '\n' +
    'CPC = Amount Spent / Total Promoted Clicks',
  cpm:
    'The average cost per 1,000 impressions on your promoted posts\n' +
    '\n' +
    'CPM = Amount Spent / Total Promoted Impressions × 1,000',
  ctr:
    'The percentage of times people clicked your promoted posts after seeing them\n' +
    '\n' +
    'CTR = Total Clicks / Promoted Impressions',
  effectivenessNonVideo:
    "The rate of this post's engagement based on the unique accounts reached\n" +
    '\n' +
    'Effectiveness = (Likes + Comments + Saves + Shares) / Reach',
  effectivenessVideo:
    "The rate of this post's views and engagement based on the unique accounts reached\n" +
    '\n' +
    'Effectiveness Rate = (Total Engagements + Video Views) / Reach',
  effectivenessOrganic:
    'The percentage of users that saw your organic post and engaged with it\n' +
    '\n' +
    'Effectiveness = Engagements / Reach',
  effectivenessPromoted:
    'The percentage of users that saw your promoted post and engaged with it\n' +
    '\n' +
    'Effectiveness = Engagements / Reach',
  engagementsOrganic:
    'The total number of organic engagements on this post\n' +
    '\n' +
    'Engagements = Reactions + Comments + Shares + Post Clicks',
  engagementsPromoted:
    'The total number of promoted engagements on this post\n' +
    '\n' +
    'Engagements = Reactions + Comments + Shares\n',
  engagementRate:
    'The average organic engagement rate for this post\n' +
    '\n' +
    'Engagement Rate = Engagements / Impressions',
  engagementRateOrganic:
    'The engagement rate for your organic post\n\nEngagement Rate = Engagements / Views',
  engagementRatePromoted:
    'The average promoted engagement rate for this post\n' +
    '\n' +
    'Engagement Rate = Promoted Engagements / Promoted  Impressions',
  frequency: 'The average number of times people saw your promoted posts',
  impressions: 'The total number of impressions this post received',
  pageLikes: 'The total number of likes this post received',
  reach:
    'The total number of unique accounts that saw this post.\n\n' +
    "Posts promoted from multiple ad accounts can't have reach totalled. The Meta API does not provide total reach. As a result, the sum of organic and promoted reach may be higher than what you see in Meta.",
  reachOrganic: 'The number of unique accounts that saw your organic posts',
  reachPromoted: 'The number of unique accounts that saw your promoted posts',
  // TODO(fbMetricsDeprecation2026): once the flag is launched, these two
  // viewers tooltips become the permanent replacements for `reach` /
  // `reachPromoted` above. Inline the strings into the metric definitions and
  // drop this comment.
  viewersTotal: ({ datePosted } = {}) => {
    const tooltip = 'The number of unique accounts that have viewed this post at least once';
    if (datePosted?.isBefore('2025-07-31', 'day')) {
      return `${tooltip}\n\nNote: Before July 31, 2025, this value is legacy Reach.`;
    }
    return tooltip;
  },
  viewersPromoted:
    'The number of unique accounts that have viewed this post through paid promotions at least once' +
    '\n\n' +
    'Note: This metric uses Reach from Ad campaigns',
  reactions:
    'The number of reactions your posts received. Reactions include Like, Love, Care, Haha, Wow, Sad, and Angry.',
  reactionsOrganic:
    'The number of organic reactions your posts received. Reactions include Like, Love, Care, Haha, Wow, Sad, and Angry.',
  reactionsPromoted:
    'The number of promoted reactions your posts received. Reactions include Like, Love, Care, Haha, Wow, Sad, and Angry.',
  shares: 'The number of times your posts were shared',
  sharesOrganic: 'The number of times your organic posts were shared',
  sharesPromoted: 'The number of times your promoted posts were shared',
  thruPlays: 'The number of times your posts were watched to completion or for at least 15 seconds',
  videoCompleteViews:
    'The number of times your videos were played from the beginning to 95% or more of their length',
  videoCompleteViewsOrganic:
    'The number of times your organic videos were played from the beginning to 95% or more of their length',
  videoCompleteViewsPromoted:
    'The number of times your promoted videos were played from the beginning to 95% or more of their length',
  videoPlays25: 'The total number of times your promoted video was played to 25% of its length',
  videoPlays50: 'The total number of times your promoted video was played to 50% of its length',
  videoPlays75: 'The total number of times your promoted video was played to 75% of its length',
  videoPlays100: 'The total number of times your promoted video was played to 100% of its length',
  videoViews: 'The number of times your videos were viewed',
  videoViewsOrganic:
    'The number of times your organic videos were played for at least 3 seconds, or for nearly their total length if shorter than 3 seconds. This metric excludes replays.',
  videoViewsPromoted:
    'The number of times your promoted videos were played for at least 3 seconds, or for nearly their total length if shorter than 3 seconds. This metric excludes replays.',
  paidAndOrganicViewsVideos:
    'The number of times your organic and promoted videos were played for at least 1ms',
  organicViewsVideos: 'The number of times your organic videos were played for at least 1ms',
  paidViewsVideos: 'The number of times your promoted videos were played for at least 1ms',
  paidAndOrganicViewsNonVideos:
    'The number of times your organic and promoted photo and text posts were displayed on screen',
  organicViewsNonVideos:
    'The number of times your organic photo and text posts were displayed on screen',
  paidViewsNonVideos:
    'The number of times your promoted photo and text posts were displayed on screen',
  // Individual reaction type tooltips
  reactionAngry: 'The number of times people used an Angry reaction to your post',
  reactionHaha: 'The number of times people used a Haha reaction to your post',
  reactionLike: 'The number of times people used a Like reaction to your post',
  reactionLove: 'The number of times people used a Love reaction to your post',
  reactionSad: 'The number of times people used a Sad reaction to your post',
  reactionWow: 'The number of times people used a Wow reaction to your post',
};

export const defaultGraphMetric = 'TOTAL_ENGAGEMENTS';

export const filterTypes = {
  GRAPH: 'Graph',
  DATE: 'Date',
  SCALE: 'Scale',
};
export const timeScaleTypes = {
  DAILY: {
    value: 'DAILY',
    label: 'By Day',
  },
  MONTHLY: {
    value: 'MONTHLY',
    label: 'By Month',
  },
};

export const facebookMediaCardMetricsAds = [
  'paidAndOrganicEngagementRate',
  'paidAndOrganicImpressions',
  'paidAndOrganicEngagements',
  'paidAndOrganicReach',
];

export const facebookMediaCardDefaults = {
  metrics: ['engagementRate', 'impressions', 'totalEngagements', 'reach'],
  labels: {
    engagementRate: 'Engagement Rate',
    totalEngagements: 'Total Engagements',
    paidAndOrganicViews: 'Views',
  },
};

export const facebookCompetitiveMediaCardDefaults = {
  labels: {
    comments: 'Comments',
    engagementRatePublic: 'Engagement Rate',
    reactions: 'Reactions',
    shares: 'Shares',
  },
  metrics: ['comments', 'engagementRatePublic', 'reactions', 'shares'],
};

export const yourPostsSortOptions = [
  {
    value: 'FACEBOOK_REELS_FB_REELS_TOTAL_PLAYS',
    label: 'All Plays',
    field: 'reel.fbReelsTotalPlays',
  },
  {
    value: 'FACEBOOK_REELS_POST_VIDEO_AVG_TIME_WATCHED',
    label: 'Avg. Time Watched',
    field: 'reel.postVideoAvgTimeWatched',
  },
  {
    value: 'FACEBOOK_COMMENTS',
    label: 'Comments',
    field: 'comments',
  },
  {
    value: 'FACEBOOK_EFFECTIVENESS',
    label: 'Effectiveness',
    field: 'effectiveness',
  },
  {
    value: 'FACEBOOK_REELS_EFFECTIVENESS',
    label: 'Effectiveness - Reels',
    field: 'reel.effectiveness',
  },
  {
    value: 'FACEBOOK_ENGAGEMENT_RATE',
    label: 'Engagement Rate',
    field: 'engagementRate',
  },
  {
    value: 'FACEBOOK_IMPRESSIONS',
    label: 'Impressions',
    field: 'impressions',
  },
  {
    value: 'FACEBOOK_LINK_CLICKS',
    label: 'Link Clicks - Total',
    field: 'linkClicks',
  },
  {
    value: 'FACEBOOK_OTHER_CLICKS',
    label: 'Other Clicks',
    field: 'otherClicks',
  },
  {
    value: 'FACEBOOK_PHOTO_VIEW_CLICKS',
    label: 'Photo View Clicks',
    field: 'photoViewClicks',
  },
  {
    value: 'FACEBOOK_BLUE_REELS_PLAYS_COUNT',
    label: 'Plays',
    field: 'reel.blueReelsPlayCount',
  },
  {
    value: 'FACEBOOK_POST_CLICKS',
    label: 'Post Clicks',
    field: 'postClicks',
  },
  {
    value: 'FACEBOOK_REACH',
    label: 'Reach',
    field: 'reach',
  },
  {
    value: 'FACEBOOK_REACTIONS',
    label: 'Reactions',
    field: 'reactions',
  },
  {
    value: 'DATE',
    label: 'Recently Published',
  },
  {
    value: 'FACEBOOK_REELS_REPLAY_COUNT',
    label: 'Replays',
    field: 'reel.fbReelsReplayCount',
  },
  {
    value: 'FACEBOOK_SHARES',
    label: 'Shares',
    field: 'shares',
  },
  {
    value: 'FACEBOOK_TOTAL_ENGAGEMENTS',
    label: 'Total Engagements',
    field: 'totalEngagements',
  },
  {
    value: 'FACEBOOK_VIDEO_COMPLETE_VIEWS',
    label: 'Video Complete Views',
    field: 'videoCompleteViews',
  },
  {
    value: 'FACEBOOK_VIDEO_VIEWS',
    label: 'Video Views',
    field: 'videoViews',
  },
];

export const facebookGalleryCardDefaults = {
  metrics: [
    'fbTotalEngagements',
    'fbAvgEngagementRate',
    'fbAvgEffectiveness',
    'fbAvgReach',
    'fbTotalImpressions',
  ],
  labels: {
    fbTotalEngagements: 'Total Engagements',
    fbAvgEngagementRate: 'Avg. Engagement Rate',
    fbAvgEffectiveness: 'Avg. Effectiveness',
    fbAvgReach: 'Avg. Reach',
    fbTotalImpressions: 'Impressions',
  },
};

export const facebookMetricFormats = {
  fbAvgEngagementRate: 'percent',
  fbAvgEffectiveness: 'percent',
};

const routeNames = {
  YOUR_POSTS: 'facebook.yourPosts',
};

export default {
  routeNames,
  facebookMediaCardDefaults,
};

export const facebookCommentRequiredScopes = [
  'pages_manage_engagement',
  'pages_read_engagement',
  'pages_manage_metadata',
];

export const facebookDirectMessageRequiredScopes = ['pages_messaging', 'pages_manage_metadata'];

export const facebookTopLineStatsMetrics = [
  {
    keys: {
      netNew: 'NET_NEW_FOLLOWERS',
      total: 'TOTAL_FOLLOWERS',
    },
    value: 'NET_NEW_FOLLOWERS',
    label: 'Net New Followers',
    tooltip: tooltips.netNewFollowers,
  },
  {
    keys: {
      organic: 'ORGANIC_ENGAGEMENTS_POSTS_V2',
      promoted: 'PAID_ENGAGEMENTS_POSTS',
      total: 'TOTAL_ENGAGEMENTS_POSTS_V2',
    },
    value: 'TOTAL_ENGAGEMENTS_POSTS_V2',
    label: 'Engagements - Total',
    tooltip: promotedTopLineStatsTooltips.engagements,
  },
  {
    keys: {
      organic: 'AVG_ORGANIC_ENGAGEMENT_RATE_V2',
      promoted: 'AVG_PAID_ENGAGEMENT_RATE_V2',
      total: 'AVG_ENGAGEMENT_RATE_V2',
    },
    value: 'AVG_ENGAGEMENT_RATE_V2',
    label: 'Avg. Eng. Rate - Total',
    percent: true,
    tooltip: promotedTopLineStatsTooltips.avgEngagementRate,
  },
  {
    keys: {
      organic: 'AVG_ORGANIC_EFFECTIVENESS_V2',
      promoted: 'AVG_PAID_EFFECTIVENESS_V2',
      total: 'AVG_EFFECTIVENESS_V2',
    },
    value: 'AVG_EFFECTIVENESS_V2',
    label: 'Avg. Effectiveness - Total',
    percent: true,
    tooltip: promotedTopLineStatsTooltips.avgEffectiveness,
  },
  {
    keys: {
      organic: 'AVG_ORGANIC_POST_REACH',
      promoted: 'AVG_PAID_POST_REACH_V2',
      total: 'AVG_PAID_AND_ORGANIC_POST_REACH_V2',
    },
    value: 'AVG_PAID_AND_ORGANIC_POST_REACH_V2',
    label: 'Avg. Reach - Total',
    tooltip: promotedTopLineStatsTooltips.avgReach,
    predicate: omitWhenFeatureFlag('fbMetricsDeprecation2026'),
  },
  {
    keys: {
      total: 'AVG_PAID_AND_ORGANIC_POST_REACH_V2',
    },
    value: 'AVG_PAID_AND_ORGANIC_POST_REACH_V2',
    label: 'Avg. Viewers - Total',
    tooltip: promotedTopLineStatsTooltips.avgViewers,
    predicate: requireFeatureFlag('fbMetricsDeprecation2026'),
  },
  {
    keys: {
      organic: 'ORGANIC_VIEWS_BY_POST',
      promoted: 'PAID_VIEWS_BY_POST',
      total: 'PAID_AND_ORGANIC_VIEWS_BY_POST',
    },
    value: 'PAID_AND_ORGANIC_VIEWS_BY_POST',
    label: 'Views - Total',
    tooltip: promotedTopLineStatsTooltips.views,
  },
  {
    keys: {
      total: 'LINK_CLICKS',
    },
    value: 'LINK_CLICKS',
    label: 'Link Clicks - Total',
    tooltip: promotedTopLineStatsTooltips.linkClicks,
  },
];

export const facebookInsightsMetrics = [
  {
    label: 'Amount Spent',
    tooltip:
      "The total amount of money you've spent on promoted posts published during the selected date range",
    metrics: {
      promoted: 'TOTAL_SPENT',
    },
  },
  {
    label: 'CPC',
    tooltip:
      'The average cost per click of your promoted posts published during the selected date range\n' +
      '\n' +
      'CPC = Amount Spent / Promoted Clicks',
    metrics: {
      promoted: 'COST_PER_CLICK',
    },
  },
  {
    label: 'CPM',
    tooltip:
      'The average cost per 1,000 impressions on your promoted posts published during the selected date range\n' +
      '\n' +
      'CPM = Amount Spent / Total Promoted Impressions x 1,000',
    metrics: {
      promoted: 'COST_PER_MILE',
    },
  },
  {
    label: 'CTR',
    tooltip:
      'The percentage of times people clicked your promoted posts published during the selected date range\n' +
      '\n' +
      'CTR = Clicks / Promoted Impressions',
    metrics: {
      promoted: 'CLICK_THROUGH_RATE',
    },
  },
  {
    label: 'Comments',
    tooltip:
      'The total number of times people commented on your promoted posts published during the selected date range',
    metrics: {
      promoted: 'PAID_COMMENTS',
      organic: 'ORGANIC_COMMENTS_POSTS_PAID',
    },
  },
  {
    label: 'Views',
    tooltip:
      'The number of times your promoted videos were played for at least 1ms plus the number of times your photo and text promoted posts were seen on screen',
    metrics: {
      promoted: 'PAID_VIEWS_BY_POST',
      organic: 'ORGANIC_VIEWS_POSTS_PAID',
    },
  },
  {
    label: 'Reactions',
    tooltip:
      'The total number of times people reacted to your promoted posts published during the selected date range',
    metrics: {
      promoted: 'PAID_REACTIONS',
      organic: 'ORGANIC_REACTIONS_POSTS_PAID',
    },
  },
  {
    label: 'Reach',
    tooltip:
      'The total number of unique accounts that saw your promoted posts published during the selected date range',
    metrics: {
      promoted: 'PAID_POST_REACH_V2',
      organic: 'ORGANIC_REACH_POSTS_PAID',
    },
    predicate: omitWhenFeatureFlag('fbMetricsDeprecation2026'),
  },
  {
    label: 'Shares',
    tooltip:
      'The total number of times people shared your promoted posts published during the selected date range',
    metrics: {
      promoted: 'PAID_SHARES',
      organic: 'ORGANIC_SHARES_POSTS_PAID',
    },
  },
  {
    label: 'Video Views',
    tooltip:
      'The total number times your promoted videos published during the reporting period were played for at least 3 seconds, or for nearly its total length if shorter than 3 seconds. This excludes replays.',
    metrics: {
      promoted: 'PAID_VIDEO_VIEWS',
      organic: 'ORGANIC_VIDEO_VIEWS_POSTS_PAID',
    },
  },
];

export const facebookPromotedMediaPopupMetricsBefore2025 = [
  {
    key: 'promotedAmountSpent',
    label: 'Amount Spent - Promoted',
    monetary: true,
    tooltip: promotedMediaPopupTooltips.amountSpent,
  },
  {
    key: 'paidAndOrganicComments',
    label: 'Comments - Total',
    tooltip: promotedMediaPopupTooltips.comments,
  },
  {
    key: 'organicComments',
    parentKey: 'paidAndOrganicComments',
    label: 'Comments - Organic',
    tooltip: promotedMediaPopupTooltips.commentsOrganic,
  },
  {
    key: 'paidComments',
    parentKey: 'paidAndOrganicComments',
    label: 'Comments - Promoted',
    tooltip: promotedMediaPopupTooltips.commentsPromoted,
  },
  {
    key: 'promotedCostPerThruPlays',
    label: 'Cost Per ThruPlay - Promoted',
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.costPerThruPlay,
    monetary: true,
  },
  {
    key: 'promotedCpc',
    label: 'CPC - Promoted',
    monetary: true,
    tooltip: promotedMediaPopupTooltips.cpc,
  },
  {
    key: 'promotedCpm',
    label: 'CPM - Promoted',
    monetary: true,
    tooltip: promotedMediaPopupTooltips.cpm,
  },
  {
    key: 'promotedCtr',
    label: 'CTR - Promoted',
    percent: true,
    tooltip: promotedMediaPopupTooltips.ctr,
  },
  {
    key: 'organicEffectiveness',
    alternativeKey: 'effectiveness',
    label: 'Effectiveness - Organic (D)',
    percent: true,
    videoMetric: false,
    tooltip: promotedMediaPopupTooltips.effectivenessNonVideo,
  },
  {
    key: 'organicEffectiveness',
    alternativeKey: 'effectiveness',
    label: 'Effectiveness - Organic (D)',
    percent: true,
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.effectivenessVideo,
  },
  {
    key: 'organicEngagementRate',
    alternativeKey: 'engagementRate',
    label: 'Engagement Rate - Organic (D)',
    percent: true,
    tooltip: promotedMediaPopupTooltips.engagementRate,
  },
  {
    key: 'paidEngagementRate',
    alternativeKey: 'promotedEngagementRate',
    label: 'Engagement Rate - Promoted (D)',
    percent: true,
    tooltip: promotedMediaPopupTooltips.engagementRatePromoted,
  },
  {
    key: 'organicEngagements',
    alternativeKey: 'totalEngagements',
    label: 'Engagements - Organic (D)',
    tooltip: promotedMediaPopupTooltips.engagementsOrganic,
  },
  {
    key: 'paidEngagements',
    alternativeKey: 'promotedEngagements',
    label: 'Engagements - Promoted',
    tooltip: promotedMediaPopupTooltips.engagementsPromoted,
  },
  {
    key: 'frequency',
    alternativeKey: 'promotedFrequency',
    label: 'Frequency - Promoted',
    forceDash: true,
    tooltip: promotedMediaPopupTooltips.frequency,
  },
  {
    scopedMetric: 'frequency',
    parentKey: 'frequency',
    scopedMetricPrefix: 'Frequency - Promoted - ',
    format: '0,0.00',
  },
  {
    key: 'paidAndOrganicImpressions',
    alternativeKey: 'promotedAndOrganicImpressions',
    label: 'Impressions - Total (D)',
    tooltip: promotedMediaPopupTooltips.impressions,
  },
  {
    key: 'organicImpressions',
    alternativeKey: 'impressions',
    parentKey: 'paidAndOrganicImpressions',
    label: 'Impressions - Organic (D)',
  },
  {
    key: 'paidImpressions',
    alternativeKey: 'promotedImpressions',
    parentKey: 'paidAndOrganicImpressions',
    label: 'Impressions - Promoted (D)',
  },
  {
    key: 'paidAndOrganicPostClicks',
    alternativeKey: 'postClicks',
    label: 'Post Clicks - Total',
  },
  {
    key: 'paidAndOrganicLinkClicks',
    alternativeKey: 'linkClicks',
    parentKey: 'paidAndOrganicPostClicks',
    label: 'Link Clicks - Total',
  },
  {
    key: 'paidAndOrganicOtherClicks',
    alternativeKey: 'otherClicks',
    parentKey: 'paidAndOrganicPostClicks',
    label: 'Other Clicks - Total',
  },
  {
    key: 'paidAndOrganicPhotoViewClicks',
    alternativeKey: 'photoViewClicks',
    parentKey: 'paidAndOrganicPostClicks',
    label: 'Photo View Clicks - Total',
    reelMetric: false,
  },
  {
    key: 'paidAndOrganicReach',
    alternativeKey: 'promotedAndOrganicReach',
    label: 'Reach - Total',
    tooltip: promotedMediaPopupTooltips.reach,
  },
  {
    key: 'organicReach',
    alternativeKey: 'reach',
    parentKey: 'paidAndOrganicReach',
    label: 'Reach - Organic',
    tooltip: promotedMediaPopupTooltips.reachOrganic,
  },
  {
    key: 'paidReach',
    alternativeKey: 'promotedReach',
    parentKey: 'paidAndOrganicReach',
    label: 'Reach - Promoted',
    tooltip: promotedMediaPopupTooltips.reachPromoted,
  },
  {
    scopedMetric: 'reach',
    parentKey: 'paidAndOrganicReach',
    scopedMetricPrefix: 'Reach - Promoted - ',
  },
  {
    key: 'paidAndOrganicReactions',
    alternativeKey: 'promotedAndOrganicReactions',
    label: 'Reactions - Total',
    tooltip: promotedMediaPopupTooltips.reactions,
  },
  {
    key: 'organicReactions',
    alternativeKey: 'reactions',
    parentKey: 'paidAndOrganicReactions',
    label: 'Reactions - Organic',
    tooltip: promotedMediaPopupTooltips.reactionsOrganic,
  },
  {
    key: 'paidReactions',
    alternativeKey: 'promotedReactions',
    parentKey: 'paidAndOrganicReactions',
    label: 'Reactions - Promoted',
    tooltip: promotedMediaPopupTooltips.reactionsPromoted,
  },
  {
    key: 'paidAndOrganicShares',
    alternativeKey: 'promotedAndOrganicShares',
    label: 'Shares - Total',
    tooltip: promotedMediaPopupTooltips.shares,
  },
  {
    key: 'organicShares',
    alternativeKey: 'shares',
    parentKey: 'paidAndOrganicShares',
    label: 'Shares - Organic',
    tooltip: promotedMediaPopupTooltips.sharesOrganic,
  },
  {
    key: 'paidShares',
    alternativeKey: 'promotedShares',
    parentKey: 'paidAndOrganicShares',
    label: 'Shares - Promoted',
    tooltip: promotedMediaPopupTooltips.sharesPromoted,
  },
  {
    key: 'thruplays',
    alternativeKey: 'promotedThruplays',
    label: 'ThruPlays - Promoted',
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.thruPlays,
  },
  {
    key: 'paidAndOrganicVideoCompleteViews',
    label: 'Video Complete Views - Total',
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.videoCompleteViews,
  },
  {
    key: 'organicVideoCompleteViews',
    alternativeKey: 'videoCompleteViews',
    parentKey: 'paidAndOrganicVideoCompleteViews',
    label: 'Video Complete Views - Organic',
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.videoCompleteViewsOrganic,
  },
  {
    key: 'paidVideoCompleteViews',
    alternativeKey: 'promotedVideoCompleteViews',
    parentKey: 'paidAndOrganicVideoCompleteViews',
    label: 'Video Complete Views - Promoted',
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.videoCompleteViewsPromoted,
  },
  {
    key: 'promotedVideoPlays25',
    label: 'Video Plays 25% - Promoted',
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.videoPlays25,
  },
  {
    key: 'promotedVideoPlays50',
    label: 'Video Plays 50% - Promoted',
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.videoPlays50,
  },
  {
    key: 'promotedVideoPlays75',
    label: 'Video Plays 75% - Promoted',
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.videoPlays75,
  },
  {
    key: 'promotedVideoPlays100',
    label: 'Video Plays 100% - Promoted',
    // 100 is being sorted before other video plays metrics, so we need to set a sort label
    sortLabel: 'Video Plays 99%+',
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.videoPlays100,
  },
  {
    key: 'paidAndOrganicVideoViews',
    alternativeKey: 'promotedAndOrganicVideoViews',
    label: 'Video Views - Total',
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.videoViews,
  },
  {
    key: 'organicVideoViews',
    alternativeKey: 'videoViews',
    label: 'Video Views - Organic',
    parentKey: 'paidAndOrganicVideoViews',
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.videoViewsOrganic,
  },
  {
    key: 'paidVideoViews',
    alternativeKey: 'promotedVideoViews',
    label: 'Video Views - Promoted',
    parentKey: 'paidAndOrganicVideoViews',
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.videoViewsPromoted,
  },
];

export const facebookPromotedMediaPopupMetrics = [
  {
    key: 'promotedAmountSpent',
    label: 'Amount Spent - Promoted',
    monetary: true,
    tooltip: promotedMediaPopupTooltips.amountSpent,
  },
  {
    key: 'paidAndOrganicComments',
    alternativeKey: 'promotedAndOrganicComments',
    label: 'Comments - Total',
    tooltip: promotedMediaPopupTooltips.comments,
  },
  {
    key: 'organicComments',
    parentKey: 'paidAndOrganicComments',
    label: 'Comments - Organic',
    tooltip: promotedMediaPopupTooltips.commentsOrganic,
  },
  {
    key: 'paidComments',
    alternativeKey: 'promotedComments',
    parentKey: 'paidAndOrganicComments',
    label: 'Comments - Promoted',
    tooltip: promotedMediaPopupTooltips.commentsPromoted,
  },
  {
    key: 'promotedCostPerThruPlays',
    label: 'Cost Per ThruPlay - Promoted',
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.costPerThruPlay,
    monetary: true,
  },
  {
    key: 'promotedCpc',
    label: 'CPC - Promoted',
    monetary: true,
    tooltip: promotedMediaPopupTooltips.cpc,
  },
  {
    key: 'promotedCpm',
    label: 'CPM - Promoted',
    monetary: true,
    tooltip: promotedMediaPopupTooltips.cpm,
  },
  {
    key: 'promotedCtr',
    label: 'CTR - Promoted',
    percent: true,
    tooltip: promotedMediaPopupTooltips.ctr,
  },
  {
    key: 'paidAndOrganicEffectivenessV2',
    label: 'Effectiveness - Total',
    percent: true,
  },
  {
    key: 'organicEffectivenessV2',
    label: 'Effectiveness - Organic',
    parentKey: 'paidAndOrganicEffectivenessV2',
    percent: true,
    tooltip: promotedMediaPopupTooltips.effectivenessOrganic,
  },
  {
    key: 'paidEffectivenessV2',
    label: 'Effectiveness - Promoted',
    parentKey: 'paidAndOrganicEffectivenessV2',
    percent: true,
    tooltip: promotedMediaPopupTooltips.effectivenessPromoted,
  },
  {
    key: 'paidAndOrganicEngagementRateV2',
    label: 'Engagement Rate - Total',
    percent: true,
  },
  {
    key: 'organicEngagementRateV2',
    label: 'Engagement Rate - Organic',
    parentKey: 'paidAndOrganicEngagementRateV2',
    percent: true,
    tooltip: promotedMediaPopupTooltips.engagementRateOrganic,
  },
  {
    key: 'paidEngagementRateV2',
    label: 'Engagement Rate - Promoted',
    parentKey: 'paidAndOrganicEngagementRateV2',
    percent: true,
    tooltip: promotedMediaPopupTooltips.engagementRatePromoted,
  },
  {
    key: 'paidAndOrganicEngagementsV2',
    label: 'Engagements - Total',
  },
  {
    key: 'organicEngagementsV2',
    label: 'Engagements - Organic',
    parentKey: 'paidAndOrganicEngagementsV2',
    tooltip: promotedMediaPopupTooltips.engagementsOrganic,
  },
  {
    key: 'paidEngagements',
    label: 'Engagements - Promoted',
    parentKey: 'paidAndOrganicEngagementsV2',
    tooltip: promotedMediaPopupTooltips.engagementsPromoted,
  },
  {
    key: 'frequency',
    alternativeKey: 'promotedFrequency',
    label: 'Frequency - Promoted',
    forceDash: true,
    tooltip: promotedMediaPopupTooltips.frequency,
  },
  {
    scopedMetric: 'frequency',
    parentKey: 'frequency',
    scopedMetricPrefix: 'Frequency - Promoted - ',
    format: '0,0.00',
  },
  {
    key: 'paidAndOrganicPostClicks',
    label: 'Post Clicks - Total',
  },
  {
    key: 'paidAndOrganicLinkClicks',
    parentKey: 'paidAndOrganicPostClicks',
    label: 'Link Clicks - Total',
  },
  {
    key: 'paidAndOrganicOtherClicks',
    alternativeKey: 'otherClicks',
    parentKey: 'paidAndOrganicPostClicks',
    label: 'Other Clicks - Total',
  },
  {
    key: 'paidAndOrganicPhotoViewClicks',
    parentKey: 'paidAndOrganicPostClicks',
    label: 'Photo View Clicks - Total',
    reelMetric: false,
  },
  {
    key: 'paidAndOrganicReachV2',
    label: 'Reach - Total',
  },
  {
    key: 'organicReach',
    parentKey: 'paidAndOrganicReachV2',
    label: 'Reach - Organic',
    tooltip: promotedMediaPopupTooltips.reachOrganic,
  },
  {
    key: 'paidReachV2',
    parentKey: 'paidAndOrganicReachV2',
    label: 'Reach - Promoted',
    tooltip: promotedMediaPopupTooltips.reachPromoted,
  },
  {
    key: 'paidAndOrganicReactions',
    label: 'Reactions - Total',
    tooltip: promotedMediaPopupTooltips.reactions,
  },
  {
    key: 'organicReactions',
    parentKey: 'paidAndOrganicReactions',
    label: 'Reactions - Organic',
    tooltip: promotedMediaPopupTooltips.reactionsOrganic,
  },
  {
    key: 'paidReactions',
    parentKey: 'paidAndOrganicReactions',
    label: 'Reactions - Promoted',
    tooltip: promotedMediaPopupTooltips.reactionsPromoted,
  },
  {
    key: 'paidAndOrganicShares',
    label: 'Shares - Total',
    tooltip: promotedMediaPopupTooltips.shares,
  },
  {
    key: 'organicShares',
    parentKey: 'paidAndOrganicShares',
    label: 'Shares - Organic',
    tooltip: promotedMediaPopupTooltips.sharesOrganic,
  },
  {
    key: 'paidShares',
    parentKey: 'paidAndOrganicShares',
    label: 'Shares - Promoted',
    tooltip: promotedMediaPopupTooltips.sharesPromoted,
  },
  {
    key: 'thruplays',
    label: 'ThruPlays - Promoted',
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.thruPlays,
  },
  {
    key: 'paidAndOrganicVideoCompleteViews',
    label: 'Video Complete Views - Total',
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.videoCompleteViews,
  },
  {
    key: 'organicVideoCompleteViews',
    alternativeKey: 'videoCompleteViews',
    parentKey: 'paidAndOrganicVideoCompleteViews',
    label: 'Video Complete Views - Organic',
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.videoCompleteViewsOrganic,
  },
  {
    key: 'paidVideoCompleteViews',
    alternativeKey: 'promotedVideoCompleteViews',
    parentKey: 'paidAndOrganicVideoCompleteViews',
    label: 'Video Complete Views - Promoted',
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.videoCompleteViewsPromoted,
  },
  {
    key: 'promotedVideoPlays25',
    label: 'Video Plays 25% - Promoted',
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.videoPlays25,
  },
  {
    key: 'promotedVideoPlays50',
    label: 'Video Plays 50% - Promoted',
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.videoPlays50,
  },
  {
    key: 'promotedVideoPlays75',
    label: 'Video Plays 75% - Promoted',
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.videoPlays75,
  },
  {
    key: 'promotedVideoPlays100',
    label: 'Video Plays 100% - Promoted',
    // 100 is being sorted before other video plays metrics, so we need to set a sort label
    sortLabel: 'Video Plays 99%+',
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.videoPlays100,
  },
  {
    key: 'paidAndOrganicVideoViews',
    alternativeKey: 'promotedAndOrganicVideoViews',
    label: 'Video Views - Total',
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.videoViews,
  },
  {
    key: 'organicVideoViews',
    alternativeKey: 'videoViews',
    label: 'Video Views - Organic',
    parentKey: 'paidAndOrganicVideoViews',
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.videoViewsOrganic,
  },
  {
    key: 'paidVideoViews',
    alternativeKey: 'promotedVideoViews',
    label: 'Video Views - Promoted',
    parentKey: 'paidAndOrganicVideoViews',
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.videoViewsPromoted,
  },
  {
    key: 'paidAndOrganicViews',
    label: 'Views - Total',
    videoMetric: false,
    tooltip: promotedMediaPopupTooltips.paidAndOrganicViewsNonVideos,
  },
  {
    key: 'organicViews',
    label: 'Views - Organic',
    parentKey: 'paidAndOrganicViews',
    videoMetric: false,
    tooltip: promotedMediaPopupTooltips.organicViewsNonVideos,
  },
  {
    key: 'paidViews',
    label: 'Views - Promoted',
    parentKey: 'paidAndOrganicViews',
    videoMetric: false,
    tooltip: promotedMediaPopupTooltips.paidViewsNonVideos,
  },
  {
    key: 'paidAndOrganicViews',
    label: 'Views - Total',
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.paidAndOrganicViewsVideos,
  },
  {
    key: 'organicViews',
    label: 'Views - Organic',
    parentKey: 'paidAndOrganicViews',
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.organicViewsVideos,
  },
  {
    key: 'paidViews',
    label: 'Views - Promoted',
    parentKey: 'paidAndOrganicViews',
    videoMetric: true,
    tooltip: promotedMediaPopupTooltips.paidViewsVideos,
  },
];

export const facebookYourPostsSortingOptions = {
  // Deprecated Metrics
  organicMetricsDeprecated: [
    METRICS.FACEBOOK.ORGANIC_COMMENTS,
    METRICS.FACEBOOK.ORGANIC_EFFECTIVENESS,
    METRICS.FACEBOOK.ORGANIC_ENGAGEMENT_RATE,
    METRICS.FACEBOOK.ORGANIC_ENGAGEMENTS,
    METRICS.FACEBOOK.ORGANIC_IMPRESSIONS_DEPRECATED,
    METRICS.FACEBOOK.OTHER_CLICKS,
    METRICS.FACEBOOK.PHOTO_VIEW_CLICKS,
    METRICS.FACEBOOK.POST_CLICKS,
    METRICS.FACEBOOK.ORGANIC_REACH,
    METRICS.FACEBOOK.ORGANIC_REACTIONS,
    METRICS.FACEBOOK.REELS_POST_VIDEO_AVG_TIME_WATCHED,
    METRICS.FACEBOOK.REELS_EFFECTIVENESS,
    METRICS.FACEBOOK.BLUE_REELS_PLAYS_COUNT,
    METRICS.FACEBOOK.REELS_REPLAY_COUNT,
    METRICS.FACEBOOK.REELS_FB_REELS_TOTAL_PLAYS,
    METRICS.FACEBOOK.ORGANIC_SHARES,
    METRICS.FACEBOOK.ORGANIC_VIDEO_COMPLETE_VIEWS,
    METRICS.FACEBOOK.ORGANIC_VIDEO_VIEWS,
  ],

  organicAndPromotedMetricsDeprecated: [
    METRICS.FACEBOOK.PAID_AND_ORGANIC_COMMENTS,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_IMPRESSIONS_DEPRECATED,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_REACH,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_REACTIONS,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_VIDEO_VIEWS,
  ],

  promotedMetricsDeprecated: [
    METRICS.FACEBOOK.PAID_COMMENTS,
    METRICS.FACEBOOK.CPC,
    METRICS.FACEBOOK.CPM,
    METRICS.FACEBOOK.CTR,
    METRICS.FACEBOOK.PAID_ENGAGEMENT_RATE,
    METRICS.FACEBOOK.PAID_ENGAGEMENTS,
    METRICS.FACEBOOK.PAID_IMPRESSIONS_DEPRECATED,
    METRICS.FACEBOOK.PAID_REACH,
    METRICS.FACEBOOK.PAID_REACTIONS,
    METRICS.FACEBOOK.PAID_VIDEO_VIEWS,
  ],

  // New Metrics
  // Ads Connected
  organicMetricsAdsConnected: [
    METRICS.FACEBOOK.ORGANIC_COMMENTS,
    METRICS.FACEBOOK.ORGANIC_EFFECTIVENESS_V2,
    METRICS.FACEBOOK.ORGANIC_ENGAGEMENT_RATE_V2,
    METRICS.FACEBOOK.ORGANIC_ENGAGEMENTS_V2,
    METRICS.FACEBOOK.ORGANIC_VIEWS,
    METRICS.FACEBOOK.ORGANIC_REACH,
    METRICS.FACEBOOK.ORGANIC_REACTIONS,
    METRICS.FACEBOOK.ORGANIC_SHARES,
    METRICS.FACEBOOK.ORGANIC_VIDEO_COMPLETE_VIEWS,
    METRICS.FACEBOOK.ORGANIC_VIDEO_VIEWS,
  ],

  organicAndPromotedMetricsAdsConnected: [
    METRICS.FACEBOOK.PAID_AND_ORGANIC_COMMENTS,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_VIEWS,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_REACH_V2,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_REACTIONS,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_VIDEO_COMPLETE_VIEWS,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_VIDEO_VIEWS,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_LINK_CLICKS,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_OTHER_CLICKS,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_PHOTO_VIEW_CLICKS,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_POST_CLICKS,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_REELS_POST_VIDEO_AVG_TIME_WATCHED,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_BLUE_REELS_PLAYS_COUNT,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_ENGAGEMENTS_V2,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_ENGAGEMENT_RATE_V2,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_EFFECTIVENESS_V2,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_REELS_REPLAY_COUNT,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_SHARES,
    METRICS.FACEBOOK.REELS_POST_IMPRESSIONS_UNIQUE,
    METRICS.FACEBOOK.REELS_POST_VIDEO_FOLLOWERS,
    METRICS.FACEBOOK.REELS_POST_VIDEO_VIEW_TIME,
    METRICS.FACEBOOK.REELS_REACTION_ANGRY,
    METRICS.FACEBOOK.REELS_REACTION_HAHA,
    METRICS.FACEBOOK.REELS_REACTION_LIKE,
    METRICS.FACEBOOK.REELS_REACTION_LOVE,
    METRICS.FACEBOOK.REELS_REACTION_SAD,
    METRICS.FACEBOOK.REELS_REACTION_WOW,
  ],

  promotedMetricsAdsConnected: [
    METRICS.FACEBOOK.AMOUNT_SPEND,
    METRICS.FACEBOOK.PAID_COMMENTS,
    METRICS.FACEBOOK.COST_PER_THRUPLAYS,
    METRICS.FACEBOOK.CPC,
    METRICS.FACEBOOK.CPM,
    METRICS.FACEBOOK.CTR,
    METRICS.FACEBOOK.FREQUENCY,
    METRICS.FACEBOOK.PAID_EFFECTIVENESS_V2,
    METRICS.FACEBOOK.PAID_ENGAGEMENT_RATE_V2,
    METRICS.FACEBOOK.PAID_ENGAGEMENTS,
    METRICS.FACEBOOK.PAID_VIEWS,
    METRICS.FACEBOOK.PAID_REACH_V2,
    METRICS.FACEBOOK.PAID_REACTIONS,
    METRICS.FACEBOOK.PAID_SHARES,
    METRICS.FACEBOOK.PAID_VIDEO_COMPLETE_VIEWS,
    METRICS.FACEBOOK.PAID_VIDEO_VIEWS,
    METRICS.FACEBOOK.THRUPLAYS,
    METRICS.FACEBOOK.VIDEO_PLAYS_25,
    METRICS.FACEBOOK.VIDEO_PLAYS_50,
    METRICS.FACEBOOK.VIDEO_PLAYS_75,
  ],

  discontinuedMetricsAdsConnected: [
    METRICS.FACEBOOK.ORGANIC_IMPRESSIONS,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_IMPRESSIONS,
    METRICS.FACEBOOK.PAID_IMPRESSIONS,
  ],

  // Unconnected Ads
  organicMetricsUnconnectedAds: [
    METRICS.FACEBOOK.ORGANIC_VIDEO_COMPLETE_VIEWS,
    METRICS.FACEBOOK.ORGANIC_VIDEO_VIEWS,
  ],

  organicAndPromotedMetricsUnconnectedAds: [
    METRICS.FACEBOOK.PAID_AND_ORGANIC_COMMENTS,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_VIEWS,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_REACH_V2,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_REACTIONS,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_VIDEO_COMPLETE_VIEWS,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_VIDEO_VIEWS,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_LINK_CLICKS,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_OTHER_CLICKS,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_PHOTO_VIEW_CLICKS,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_POST_CLICKS,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_REELS_POST_VIDEO_AVG_TIME_WATCHED,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_BLUE_REELS_PLAYS_COUNT,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_ENGAGEMENTS_V2,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_ENGAGEMENT_RATE_V2,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_EFFECTIVENESS_V2,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_REELS_REPLAY_COUNT,
    METRICS.FACEBOOK.PAID_AND_ORGANIC_SHARES,
    METRICS.FACEBOOK.REELS_POST_IMPRESSIONS_UNIQUE,
    METRICS.FACEBOOK.REELS_POST_VIDEO_FOLLOWERS,
    METRICS.FACEBOOK.REELS_POST_VIDEO_VIEW_TIME,
    METRICS.FACEBOOK.REELS_REACTION_ANGRY,
    METRICS.FACEBOOK.REELS_REACTION_HAHA,
    METRICS.FACEBOOK.REELS_REACTION_LIKE,
    METRICS.FACEBOOK.REELS_REACTION_LOVE,
    METRICS.FACEBOOK.REELS_REACTION_SAD,
    METRICS.FACEBOOK.REELS_REACTION_WOW,
  ],

  discontinuedMetricsUnconnectedAds: [METRICS.FACEBOOK.ORGANIC_IMPRESSIONS],
};

export const facebookYourPostsMediaCardDisplayMetrics = [
  METRICS.FACEBOOK.PAID_AND_ORGANIC_ENGAGEMENT_RATE_V2,
  METRICS.FACEBOOK.PAID_AND_ORGANIC_ENGAGEMENTS_V2,
  METRICS.FACEBOOK.PAID_AND_ORGANIC_REACH_V2,
  METRICS.FACEBOOK.PAID_AND_ORGANIC_VIEWS,
];

export const facebookOverviewMetricChartTitleOptions = [
  {
    label: 'Avg. Eng. Rate - Total',
    value: 'AVG_ENGAGEMENT_RATE_V2',
    format: 'percent',
    displayTotalLabel: false,
  },
  {
    label: 'Avg. Effectiveness - Total',
    value: 'AVG_EFFECTIVENESS_V2',
    format: 'percent',
    displayTotalLabel: false,
  },
  { label: 'Engagements - Total', value: 'TOTAL_ENGAGEMENTS_POSTS_V2' },
  { label: 'Link Clicks - Total', value: 'LINK_CLICKS' },
  {
    label: 'Reach - Total',
    value: 'PAID_AND_ORGANIC_POST_REACH_V2',
    predicate: omitWhenFeatureFlag('fbMetricsDeprecation2026'),
  },
  {
    label: 'Avg. Viewers - Total',
    value: 'AVG_PAID_AND_ORGANIC_POST_REACH_V2',
    displayTotalLabel: false,
    predicate: requireFeatureFlag('fbMetricsDeprecation2026'),
  },
  { label: 'Views - Total', value: 'PAID_AND_ORGANIC_VIEWS_BY_POST' },
];

export const overviewCSVConfig = {
  metrics: {
    label: 'Metrics CSV',
    filenamePrefix: 'metrics_csv',
    metricsListOrganicOnlyDeprecated: [
      'TOTAL_ENGAGEMENTS',
      'AVG_ENGAGEMENT_RATE',
      'AVG_EFFECTIVENESS',
      'AVG_REACH',
      'IMPRESSIONS',
      'LINK_CLICKS',
    ],
    metricsListPromotedBreakdownDeprecated: [
      'ORGANIC_ENGAGEMENTS_POSTS',
      'AVG_ORGANIC_ENGAGEMENT_RATE',
      'AVG_ORGANIC_EFFECTIVENESS',
      'AVG_ORGANIC_POST_REACH',
      'AVG_PAID_POST_REACH',
      'AVG_PAID_AND_ORGANIC_POST_REACH',
      'ORGANIC_IMPRESSIONS_BY_POST',
      'PAID_IMPRESSIONS_BY_POST',
      'PAID_AND_ORGANIC_IMPRESSIONS_BY_POST',
      'LINK_CLICKS',
    ],
    metricsListOrganicOnly: [
      'NET_NEW_FOLLOWERS',
      'TOTAL_ENGAGEMENTS_POSTS_V2',
      'AVG_ENGAGEMENT_RATE_V2',
      'AVG_EFFECTIVENESS_V2',
      'AVG_PAID_AND_ORGANIC_POST_REACH_V2',
      'PAID_AND_ORGANIC_VIEWS_BY_POST',
      'LINK_CLICKS',
      'PAID_AND_ORGANIC_IMPRESSIONS_BY_POST',
    ],
    metricsListPromotedBreakdown: [
      'NET_NEW_FOLLOWERS',
      'TOTAL_FOLLOWERS',

      'ORGANIC_ENGAGEMENTS_POSTS_V2',
      'PAID_ENGAGEMENTS_POSTS',
      'TOTAL_ENGAGEMENTS_POSTS_V2',

      'AVG_ORGANIC_ENGAGEMENT_RATE_V2',
      'AVG_PAID_ENGAGEMENT_RATE_V2',
      'AVG_ENGAGEMENT_RATE_V2',

      'AVG_ORGANIC_EFFECTIVENESS_V2',
      'AVG_PAID_EFFECTIVENESS_V2',
      'AVG_EFFECTIVENESS_V2',

      'AVG_ORGANIC_POST_REACH',
      'AVG_PAID_POST_REACH_V2',
      'AVG_PAID_AND_ORGANIC_POST_REACH_V2',

      'ORGANIC_VIEWS_BY_POST',
      'PAID_VIEWS_BY_POST',
      'PAID_AND_ORGANIC_VIEWS_BY_POST',

      'LINK_CLICKS',

      'ORGANIC_IMPRESSIONS_BY_POST',
      'PAID_IMPRESSIONS_BY_POST',
      'PAID_AND_ORGANIC_IMPRESSIONS_BY_POST',
    ],
  },
};
